import { useState, type FormEvent } from 'react'
import { supabase } from '../lib/supabase'

/**
 * CHOOSING A NEW PASSWORD after arriving from a reset link.
 *
 * Supabase signs the person in the moment they open the link, so at this point
 * they hold a real session and could technically use the app. They must not be
 * allowed to wander off and forget: a recovery session that is never converted
 * into a password leaves them locked out again tomorrow, and they will
 * reasonably conclude the reset "did not work".
 *
 * Hence the whole screen, no way past it but forward, and no Cancel.
 */

export function SetPasswordScreen({ email }: { email: string | null }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Eight is Supabase's own minimum. Anything more elaborate — symbols, mixed
  // case — reliably produces a password written on the inside of a phone case,
  // which is worse than a long simple one.
  const tooShort = password.length > 0 && password.length < 8
  const mismatch = confirm.length > 0 && password !== confirm
  const ready = password.length >= 8 && password === confirm

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)

    const { error } = await supabase.auth.updateUser({ password })
    setBusy(false)

    if (error) {
      setError(
        /same.*password|should be different/i.test(error.message)
          ? 'That is the password you already had. Choose a different one.'
          : error.message,
      )
      return
    }
    // The auth listener in App.tsx sees the session settle and moves on.
  }

  return (
    <div className="screen">
      <div className="appbar">
        <h1>Choose a new password</h1>
      </div>

      <div className="center-card">
        <form className="card" onSubmit={onSubmit}>
          {email && (
            <p className="muted" style={{ marginTop: 0 }}>
              For <strong>{email}</strong>
            </p>
          )}

          <label htmlFor="p1">New password</label>
          <input
            id="p1"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
          {tooShort && <p className="muted">At least 8 characters.</p>}

          <label htmlFor="p2">Type it again</label>
          <input
            id="p2"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
          {mismatch && <p className="muted">The two do not match yet.</p>}

          {error && (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          )}

          <div style={{ marginTop: 18 }}>
            <button className="btn-primary" type="submit" disabled={busy || !ready}>
              {busy ? 'Saving…' : 'Save and continue'}
            </button>
          </div>

          <p className="muted" style={{ marginTop: 16, marginBottom: 0 }}>
            Pick something long and easy for you to remember rather than short
            and clever. A password written on a phone case protects nobody.
          </p>
        </form>
      </div>
    </div>
  )
}
